<script setup lang="ts">
import { TuButton, ButtonSize, ButtonSeverity, TuTooltipWrapper } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { computed } from "vue";

//
// Setup
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

//
// Props
interface Props {
  email: string;
  invalidReason?: string;
}

const props = withDefaults(defineProps<Props>(), {
  invalidReason: "",
});

//
// Emit
interface Events {
  (e: "remove"): void;
}

const emit = defineEmits<Events>();

//
// Computed
const isMobileView = smallerOrEqual("xl");
const tooltipContent = computed(() => isMobileView.value ? "" : props.invalidReason);

//
// Methods
function onRemove(): void {
  emit("remove");
}
</script>

<template>
  <TuTooltipWrapper :tooltip="tooltipContent">
    <TuButton
      label=""
      class="invitee-chip _invitee-chip"
      :class="{
        'invitee-chip--invalid': invalidReason,
        'invitee-chip--mobile': isMobileView,
      }"
      :size="ButtonSize.MEDIUM"
      :severity="ButtonSeverity.FILTER"
      :aria-label="`Remove ${props.email}`"
      @click="onRemove"
    >
      <span class="invitee-chip__content">
        <i
          v-if="invalidReason"
          class="invitee-chip__icon invitee-chip__icon--warning _warning fa-solid fa-circle-exclamation"
          aria-hidden="true"
        />
        <span class="invitee-chip__email _email">{{ props.email }}</span>
        <i
          class="invitee-chip__icon invitee-chip__icon--remove _remove fa-solid fa-xmark"
          aria-hidden="true"
        />
      </span>
    </TuButton>
    <small
      v-if="isMobileView && invalidReason"
      class="invitee-chip__mobile-invalid-feedback _invalid_reason"
    >
      {{ invalidReason }}
    </small>
  </TuTooltipWrapper>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.invitee-chip {
  $self: &;

  &__content {
    display: inline-flex;
    gap: spacing.$xxxs;
    align-items: center;
  }

  &__email {
    font-size: typography.$xs-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$xs-line-height;
  }

  &__icon {
    font-size: typography.$xs-font-size;

    &--warning {
      font-size: typography.$base-font-size;

      color: color.$alert;
    }
  }

  &__mobile-invalid-feedback {
    font-size: typography.$xxs-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$xxs-line-height;
    color: color.$alert;
  }

  &--mobile {
    display: flex;
    flex-flow: column;
  }
}
</style>
